import { Component, computed, effect, inject, input, InputSignal, OnDestroy, signal } from '@angular/core';
import { Subject } from 'rxjs';
import {
  ClientRequestItemStatus,
  ClientRequestSubmitAnswerPayload,
  MASKED_ITEM_RESPONSE_VALUE,
  QuestionnaireResponse,
  RequestQuestionnaire,
} from '@aiwyn/api';
import { CommonClientRequestsState } from '@aiwyn/common/client-requests';
import { FormControl, Validators } from '@angular/forms';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { ClientRequestsState, ClientRequestSubmitAnswers, ClientRequestSubmitAnswersSuccess } from '../../../../../../../state';
import { DATA_SELECTOR_INJECTION_TOKEN, LoadableStatus } from '@aiwyn/shared';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, takeUntil } from 'rxjs/operators';
import { LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { AuthService } from '@aiwyn/auth';
import { NO_WHITESPACE_VALIDATOR } from 'src/app/utility/custom-validators';

@Component({
  selector: 'awn-questionnaire-item-content',
  templateUrl: './questionnaire-item-content.component.html',
  styleUrl: './questionnaire-item-content.component.scss',
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'questionnaire-item-content',
    },
  ],
  standalone: false,
})
export class QuestionnaireItemContentComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>()
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);
  private actions$ = inject(Actions);

  documentIndex: InputSignal<number> = input();
  sectionIndex: InputSignal<number> = input();

  questionnaireItem: InputSignal<RequestQuestionnaire> = input()

  readonly: InputSignal<boolean> = input();
  notApplicable: InputSignal<boolean> = input();

  textAnswer = new FormControl('', [Validators.required, NO_WHITESPACE_VALIDATOR])
  previousAnswer = '';
  hasAttemptedSubmit = signal(false);

  private readonly _launchDarklyService = inject(LaunchDarklyService);
  private readonly _authService = inject(AuthService);

  private allQuestionnaireResponses = toSignal(this.store.select(CommonClientRequestsState.getAllQuestionnaireResponses), { initialValue: [] });

  private questionnaireResponsesStatus = toSignal(this.store.select(CommonClientRequestsState.getQuestionnaireResponsesStatus), { initialValue: LoadableStatus.DEFAULT });

  // Track submission loading state
  private isSubmitting = signal(false);

  // LaunchDarkly flag for IN_REVIEW items editing
  protected isInReviewEditingEnabled = toSignal(this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_IN_REVIEW_ITEMS_EDITING), {
    initialValue: false,
  });

  // Item restriction check
  private contactId = toSignal(this._authService.getUser().pipe(map(user => user?.getContactId())));
  private isItemRestrictionsEnabled = toSignal(
    this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_CLIENT_REQUEST_ITEM_RESTRICTIONS),
    { initialValue: false },
  );

  protected isItemRestricted = computed(() => {
    const item = this.questionnaireItem();
    const flagEnabled = this.isItemRestrictionsEnabled();
    const contactId = this.contactId();
    return flagEnabled && item?.restricted === true && !item.assignees?.some(a => a.contactId === contactId);
  });

  matchingQuestionnaireResponse = computed(() => {
    const responses = this.allQuestionnaireResponses();
    const currentItem = this.questionnaireItem();

    if (!currentItem) {
      return null;
    }

    return responses.find(response => response.clientRequestItemId === currentItem.id) || null as QuestionnaireResponse;
  });

  questionnaireData = computed(() => {
    const currentItem = this.questionnaireItem();
    const responseData = this.matchingQuestionnaireResponse();

    if (responseData && responseData.isMasked) {
      return {
        answer: MASKED_ITEM_RESPONSE_VALUE,
        answerOptions: [],
        source: 'questionnaireResponses' as const,
      };
    }

    if (responseData && responseData.decryptedMetadata) {
      return {
        answer: responseData.decryptedMetadata.answer || '',
        answerOptions: responseData.decryptedMetadata.answerOptions || [],
        source: 'questionnaireResponses' as const
      };
    }

    if (currentItem.metadata) {
      return {
        answer: currentItem.metadata.answer || '',
        answerOptions: currentItem.metadata.answerOptions || [],
        source: 'metadata' as const
      };
    }

    return {
      answer: '',
      answerOptions: [],
      source: 'default' as const
    };
  });

  isLoadingQuestionnaireData = computed(() => {
    return this.questionnaireResponsesStatus() === LoadableStatus.LOADING;
  });

  isRefreshing = computed(() => {
    return this.isLoadingQuestionnaireData() || this.isSubmitting();
  });

  constructor(
    private store: Store,
  ) {
    // Listen for submit answers success to stop loading
    this.actions$.pipe(
      ofActionSuccessful(ClientRequestSubmitAnswersSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isSubmitting.set(false);
    });

    effect(() => {
      const data = this.questionnaireData();
      const currentItem = this.questionnaireItem();
      const isCurrentlyRefreshing = this.isRefreshing();

      if (data && currentItem) {
        const newAnswer = data.answer || '';

        if (this.previousAnswer !== newAnswer && this.textAnswer.value !== newAnswer) {
          this.textAnswer.setValue(newAnswer, { emitEvent: false });
          this.previousAnswer = newAnswer;
        }

        const isInReviewEditingEnabled = this.isInReviewEditingEnabled();

        const shouldDisable =
          currentItem.status === ClientRequestItemStatus.DONE ||
          (!isInReviewEditingEnabled && currentItem.status === ClientRequestItemStatus.IN_REVIEW) ||
          this.readonly() ||
          this.notApplicable() ||
          isCurrentlyRefreshing ||
          this.matchingQuestionnaireResponse()?.isMasked ||
          this.isItemRestricted();

        if (shouldDisable && this.textAnswer.enabled) {
          this.textAnswer.disable({ emitEvent: false });
        } else if (!shouldDisable && this.textAnswer.disabled) {
          this.textAnswer.enable({ emitEvent: false });
        }
      }
    });
  }

  submitValue(): void {
    // Only apply validation for IN_REVIEW items if feature flag is enabled
    const isInReview = this.questionnaireItem()?.status === ClientRequestItemStatus.IN_REVIEW;
    const isInReviewEditingEnabled = this.isInReviewEditingEnabled();

    if (isInReview && isInReviewEditingEnabled) {
      this.hasAttemptedSubmit.set(true);

      // Mark the control as touched to show validation errors
      this.textAnswer.markAsTouched();

      // Prevent submission if the field is empty or only whitespace
      if(this.textAnswer.value?.trim() === '' || this.textAnswer.invalid) {
        return;
      }
    } else {
      // For non-IN_REVIEW items or when flag is disabled, allow empty submissions (original behavior)
      if(this.textAnswer.value?.trim() === '' && this.questionnaireData().answer.trim() === '') {
        return;
      }
    }

    const selectedClientRequestId = this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);

    const payload: ClientRequestSubmitAnswerPayload = {
      clientRequestId: selectedClientRequestId,
      clientRequestItemAnswers: [
        {
          clientRequestItemId: this.questionnaireItem().id,
          metadata: {
            answer: this.textAnswer.value,
          },
        },
      ],
    };

    this.isSubmitting.set(true);
    this.hasAttemptedSubmit.set(false);
    this.store.dispatch(new ClientRequestSubmitAnswers(payload));
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
  protected readonly ClientRequestItemStatus = ClientRequestItemStatus;
}
